
import * as React from 'react';
import { IActionProps, Action } from '../action/Action';
import { IOnExecuteProps } from '../commandSet/CommandSet';
import { Signal } from '../../utilities/async/Signal';
import {
  backgroundTaskQueueControl
} from '../backgroundTaskRunner/BackgroundTaskHost';
import { IComponentDecorator, IDecoratedProps, IDecoratedComponentType } from '../../utilities/components/Decorators';
import { connectToItemCache } from './connectToItemCache';
import { IItemCache } from '../../models/IItemCacheObserver';
import { IBackgroundTaskScope } from '../../models/backgroundTasks/IBackgroundTaskQueue';
import { CancelError } from '../../utilities/async/CancelError';

class Wizard extends React.PureComponent<IWithWizardInjectedProps> {
  public render(): JSX.Element {
    return (
      <>{ this.props.children }</>
    );
  }

  public componentWillUnmount(): void {
    this.props.reject(new CancelError({ message: 'The task host for the Wizard action was unmounted before the action completed.' }));
  }
}

/**
 * @public
 */
export interface IWithWizardInjectedProps {
  resolve: (result?: Promise<void>) => void;
  reject: (error: Error) => void;
  onExecuteProps?: IOnExecuteProps;
}

/**
 * @public
 */
export type IWithWizardProps = IDecoratedProps<IActionProps, Pick<IActionProps, 'onExecute'>, {}>;

/**
 * @public
 */
export interface IWithWizardOptions {
  /**
   * Whether or not this action should be executed as a background task.
   */
  isBackgroundTask?: boolean;
  /**
   * @beta
   */
  backgroundTaskScope?: IBackgroundTaskScope;
}

/**
 * @public
 */
export type IDecorateWithWizardProps<TProps extends TInjectedProps, TInjectedProps = IWithWizardInjectedProps, TOwnProps = {}> =
  IDecoratedProps<TProps, TInjectedProps, TOwnProps & IWithWizardProps>;

/**
 * Wraps a component with a 'wizard' to manage its lifetime as an action.
 * A 'wizard' is a headless component which can orchestrate UX action
 * by rendering components which use layers.
 *
 * The wrapped component will receive `resolve` and `reject` callbacks via `props`
 * to facilitate completion (or failure or cancellation) of the action.
 *
 * @public
 */
export function withWizard<TInjectedProps = IWithWizardInjectedProps, TOwnProps = {}>(
  mapWizardToProps?: (wizard: IWithWizardInjectedProps, props: TOwnProps) => TInjectedProps,
  options: IWithWizardOptions = {}
): IComponentDecorator<TInjectedProps, TOwnProps & IWithWizardProps> {
  return <P extends TInjectedProps>(
    component: React.ComponentType<P>
  ): IDecoratedComponentType<P, TInjectedProps, TOwnProps & IWithWizardProps> => {
    const {
      isBackgroundTask,
      backgroundTaskScope: scope = isBackgroundTask ? 'global' : 'local'
    } = options;

    const Component = component;

    const WizardAction = connectToItemCache((
      itemCache: IItemCache,
      props: TOwnProps & IWithWizardProps
    ): Pick<IActionProps, 'onExecute' | 'isAvailable'> => {
      const controlHandler = itemCache.demandItemFacet(backgroundTaskQueueControl, undefined);

      const {
        queue
      } = backgroundTaskQueueControl.evaluate(controlHandler)(itemCache, {
        scope
      });

      if (!queue) {
        return {
          isAvailable: false
        };
      }

      const onExecute = async (onExecuteProps: IOnExecuteProps) => {
        const {
          isAvailable,
          isToggled,
          engagement,
          ...contentProps
        } = props as IWithWizardProps;

        const {
          promise,
          resolve,
          reject
        } = new Signal<void>();

        const wizardInjectedProps: IWithWizardInjectedProps = {
          resolve,
          reject,
          onExecuteProps: onExecuteProps
        };

        const propsFromWizard = mapWizardToProps ?
          mapWizardToProps(wizardInjectedProps, contentProps as TOwnProps) :
          wizardInjectedProps as {} as TInjectedProps;

        const content = (
          <Wizard resolve={ resolve } reject={ reject }>
            <Component
              { ...contentProps }
              { ...propsFromWizard as P }
            />
          </Wizard>
        );

        queue.enqueue({
          content,
          promise,
          scope
        });

        await promise;
      };

      return {
        onExecute
      };
    })(Action);

    return WizardAction as {} as IDecoratedComponentType<P, TInjectedProps, TOwnProps & IWithWizardProps>;
  };
}
